import { useMemo } from 'react';

import { Cloud } from '@cast/shared-types';
import { Addon, AddonsAPIProvider } from '@cast/types-omni';
import {
  Alert,
  Icons,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  tokens,
} from '@cast/design-system';
import { useField } from 'react-final-form';

import {
  RffCheckbox,
  RffControlLabel,
  RffJsonEditor,
} from '@cast/shared-rff-ui';

import { useListAddonsQuery } from 'hooks/queries/omni';

import { isOmniAddonCompatible } from '../_utils/isOmniAddonCompatible';
import { AddonFormValue, EdgeLocationFormValues } from '../types';

export const edgeLocationProviderToAddonsApiProvider = (
  provider: EdgeLocationFormValues['provider']
): AddonsAPIProvider => {
  if (provider === Cloud.aws) {
    return AddonsAPIProvider.AWS;
  }

  if (provider === Cloud.gcp) {
    return AddonsAPIProvider.GCP;
  }

  if (provider === Cloud.oracle) {
    return AddonsAPIProvider.OCI;
  }

  return AddonsAPIProvider.CUSTOM;
};

type AddonItemProps = {
  addon: Addon;
  isCompatible: boolean;
  name: string;
  testIdPrefix: string;
};

const AddonItem = ({
  addon,
  isCompatible,
  name,
  testIdPrefix,
}: AddonItemProps) => {
  const addonName = addon.name ?? '';
  const addonEnabledFieldName = `${name}.${addonName}.enabled`;
  const {
    input: { value: isAddonEnabled },
  } = useField<AddonFormValue['enabled']>(addonEnabledFieldName);

  return (
    <Stack gap={8} data-testid={`${testIdPrefix}-row-${addonName}`}>
      <RffControlLabel
        name={addonEnabledFieldName}
        disabled={!isCompatible}
        slotProps={{
          errorMessageWrapper: {
            testId: `${testIdPrefix}-error-${addonName}`,
          },
        }}
        labelWeight="medium"
        label={
          <Stack component="span" direction="row" gap={4}>
            {addon.displayName || addonName}
            {!isCompatible && (
              <Tooltip
                title={`Requires OMNI agent v${addon.minimumOmniAgentVersion} or later. Upgrade the agent on this cluster to enable this add-on.`}
                testId={`${testIdPrefix}-incompatible-tooltip-${addonName}`}
              >
                <Icons.Warning
                  size={16}
                  weight="fill"
                  color={tokens.theme.bg.status.warningStrong}
                />
              </Tooltip>
            )}
          </Stack>
        }
        description={addon.description || 'No description provided.'}
        control={
          <RffCheckbox
            name={addonEnabledFieldName}
            disabled={!isCompatible}
            testId={`${testIdPrefix}-checkbox-${addonName}`}
          />
        }
      />
      {isAddonEnabled && (
        <RffJsonEditor
          testId={`${testIdPrefix}-values-editor-${addonName}`}
          name={`${name}.${addonName}.values`}
          disabled={!isCompatible}
          label={
            <>
              Helm values
              <Typography
                component="span"
                variant="inherit"
                color={tokens.theme.fg.structural.secondary}
              >
                {' '}
                (optional)
              </Typography>
            </>
          }
          labelTooltipProps={{
            title:
              'Helm values for this add-on, as a JSON object. Merged over the add-on’s defaults, so you only set the overrides you need. Leave empty to use defaults.',
            placement: 'top',
          }}
          placeholder="{}"
          indentation={2}
          sx={{ ml: 24 }}
        />
      )}
    </Stack>
  );
};

type SelectAddonsFieldProps = {
  provider: EdgeLocationFormValues['provider'];
  name: string;
  testIdPrefix?: string;
  omniAgentVersion?: string;
};

export const SelectAddonsField = ({
  provider,
  name,
  testIdPrefix = 'addon',
  omniAgentVersion,
}: SelectAddonsFieldProps) => {
  const apiProvider = edgeLocationProviderToAddonsApiProvider(provider);
  const { addons, isLoading } = useListAddonsQuery({ provider: apiProvider });

  const addonsWithCompatibility = useMemo(
    () =>
      addons.map((addon) => ({
        addon,
        isCompatible: isOmniAddonCompatible(addon, omniAgentVersion),
      })),
    [addons, omniAgentVersion]
  );

  const incompatibleAddons = useMemo(
    () => addonsWithCompatibility.filter((entry) => !entry.isCompatible),
    [addonsWithCompatibility]
  );

  if (isLoading) {
    return (
      <Stack gap={16}>
        <Skeleton height={56} />
        <Skeleton height={56} />
        <Skeleton height={56} />
      </Stack>
    );
  }

  if (addons.length === 0) {
    return (
      <Stack data-testid={`${testIdPrefix}-select-addons-field`}>
        <Typography variant="P14R" color={tokens.theme.fg.structural.secondary}>
          No add-ons are available for the selected provider.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack gap={16} data-testid={`${testIdPrefix}-select-addons-field`}>
      {incompatibleAddons.length > 0 && (
        <Alert
          color="warning"
          size="sm"
          testId={`${testIdPrefix}-incompatible-alert`}
          sx={{ mb: 4 }}
        >
          <Typography component="span" variant="P12M">
            Some Add-ons require a newer OMNI agent and can&apos;t be selected.
            Upgrade the agent to enable them.
          </Typography>
        </Alert>
      )}
      {addonsWithCompatibility.map(({ addon, isCompatible }) => (
        <AddonItem
          key={addon.name}
          addon={addon}
          isCompatible={isCompatible}
          name={name}
          testIdPrefix={testIdPrefix}
        />
      ))}
    </Stack>
  );
};
